import { useEffect, useRef, useState } from 'react';
import { GLASS_CLASSES } from '../design-system';
import { RollingNumber } from '../reveal/RollingNumber';
import { affixFor } from '../currency';
import { SLEEK_PILL_SURFACE, useIsSleek } from '../theme';
import { useWallet } from './useWallet';

const FLASH_MS = 700;

/** Which of the two render sites this pill is. Picks the Design Constructor
 *  knob that governs its visibility, so an operator whose own top bar already
 *  shows the player balance can drop ours from the header, the reveal screen,
 *  or both. */
export type BalancePillSurface = 'header' | 'reveal';

// Written out in full (not built from `surface`) so the registry invariant
// test can see both var names in the source.
const DISPLAY_VAR: Record<BalancePillSurface, string> = {
  header: 'var(--co-BalancePill-headerDisplay, inline-flex)',
  reveal: 'var(--co-BalancePill-revealDisplay, inline-flex)',
};

// `$47,750.63` vs `$47750.63` — opt-in per operator: the comma is a US
// convention, so the default keeps the ungrouped amount live partners see.
const THOUSANDS_SEPARATOR_STYLE = {
  display: 'var(--co-BalancePill-thousandsSeparator, none)',
};

/**
 * Live wallet balance pill — shown next to `MyPacksPill` in the modern
 * header and next to the reveal screen's `Exit` button. Most casinos hide
 * their own top-bar balance while a game is open ("Balance In Play") and
 * expect the game to show it, so this is usually the player's only view of
 * their money — hence it stays visible unless an operator explicitly opts
 * out via the Design Constructor (`headerDisplay` / `revealDisplay`, per
 * render site: some hosts wrap the iframe in their own nav bar that already
 * carries the balance).
 *
 * Reuses the reveal step's `RollingNumber` odometer for the "balance just
 * changed" feel: the first real value is printed instantly (no spin on page
 * load), every value after that re-spins via a `key` bump. A credit
 * additionally gets a brief green glow (debits roll but don't flash — a red
 * "you lost money" glow read as too alarming for an ordinary purchase).
 *
 * Read-only by default. `onClick` makes it a button — the Stripe deposit
 * flow passes its top-up handler there; seamless operators have nowhere to
 * send a tap, so they get a plain display.
 */
export interface BalancePillProps {
  /** Render site — selects the operator's hide/show knob for this pill. */
  surface: BalancePillSurface;
  /** Stay at the compact (h-9) size on every breakpoint, matching
   *  `Button size="md"` — used next to the reveal screen's Exit button.
   *  Default (false) scales up at ≥600px to match `MyPacksPill` in the
   *  header. */
  fixedSize?: boolean;
  /** Makes the pill interactive. Omit for a read-only display. */
  onClick?: () => void;
}

export function BalancePill({ surface, fixedSize = false, onClick }: BalancePillProps) {
  const wallet = useWallet();
  const isSleek = useIsSleek();

  const hasInitRef = useRef(false);
  const prevCentsRef = useRef<number | null>(null);
  const [animKey, setAnimKey] = useState(0);
  const [creditFlash, setCreditFlash] = useState(false);

  useEffect(() => {
    if (wallet.balance_cents === null) return;
    if (!hasInitRef.current) {
      hasInitRef.current = true;
      prevCentsRef.current = wallet.balance_cents;
      return;
    }
    if (wallet.balance_cents === prevCentsRef.current) return;
    const isCredit = wallet.balance_cents > (prevCentsRef.current ?? 0);
    prevCentsRef.current = wallet.balance_cents;
    setAnimKey((k) => k + 1);
    if (!isCredit) return;
    setCreditFlash(true);
    const t = setTimeout(() => setCreditFlash(false), FLASH_MS);
    return () => clearTimeout(t);
  }, [wallet.balance_cents]);

  // A sale made while the header was off screen (sleek's reveal covers the
  // lobby) mounts this pill on the new figure, so it is queued and played
  // here on the way back (partner feedback 2026-10-16). The flag is cleared
  // when the flash ends, not when read, so a Strict Mode remount still finds it.
  const { hasQueuedCreditFlash, clearCreditFlash } = wallet;
  useEffect(() => {
    if (surface !== 'header' || !hasQueuedCreditFlash()) return;
    setAnimKey((k) => k + 1);
    setCreditFlash(true);
    const t = setTimeout(() => {
      setCreditFlash(false);
      clearCreditFlash();
    }, FLASH_MS);
    return () => clearTimeout(t);
  }, [surface, hasQueuedCreditFlash, clearCreditFlash]);

  const { prefix, suffix } = affixFor(wallet.currency);
  // Always show a real amount — a not-yet-loaded balance reads as $0.00 rather
  // than an em-dash placeholder, so the pill never looks empty/broken (a fresh
  // embed player genuinely is $0 until they top up).
  const displayValue = (wallet.balance_cents ?? 0) / 100;

  const glowVar = creditFlash
    ? 'var(--co-BalancePill-creditGlow, var(--color-brand-primary, #22E11C))'
    : null;

  // Sleek's header pills are flat and a step taller (partner Figma 2026-10-06).
  const className =
    (isSleek ? SLEEK_PILL_SURFACE : GLASS_CLASSES) +
    ' inline-flex items-center whitespace-nowrap font-semibold ' +
    (isSleek ? 'h-10 px-3 text-[14px] tracking-[-0.15px] ' : 'text-white h-9 px-3 text-[13px] tracking-[-0.1px] ') +
    (fixedSize
      ? ''
      : '@[600px]:h-11 @[600px]:px-5 @[600px]:text-[15px] @[600px]:tracking-[-0.3px] ') +
    'transition-[opacity,box-shadow] duration-500 ease-out ' +
    (onClick ? 'cursor-pointer hover:opacity-90' : '');

  const style = {
    display: DISPLAY_VAR[surface],
    ...(glowVar
      ? {
          boxShadow:
            `0 0 0 1.5px ${glowVar}, ` +
            `0 0 18px 2px color-mix(in srgb, ${glowVar} 55%, transparent)`,
        }
      : {}),
  };

  const content = (
    <span className="inline-flex tabular-nums">
      {prefix}
      {/* RollingNumber's glyph windows sit ~0.25em higher than normal text
          of the same font-size (Inter's em-box reserves more descender
          space than digits use) — invisible in the large standalone reveal
          numbers it was built for, but obvious here next to the literal "$"
          prefix and the neighbouring Exit button. Nudge down to match;
          scoped to this consumer rather than the shared component so other
          call sites are untouched. */}
      <span className="inline-flex translate-y-[0.25em]">
        <RollingNumber
          key={animKey}
          value={displayValue}
          start
          instant={animKey === 0}
          separatorStyle={THOUSANDS_SEPARATOR_STYLE}
        />
      </span>
      {suffix}
    </span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label="Balance — tap to add funds"
        data-co-component="BalancePill"
        style={style}
        className={className}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      role="status"
      aria-label="Balance"
      data-co-component="BalancePill"
      style={style}
      className={className}
    >
      {content}
    </div>
  );
}
